/**
 * Basket WebSocket Hook
 *
 * This hook provides real-time basket synchronization across multiple
 * tabs, devices, and team members viewing the same venue's basket.
 *
 * Events:
 * - basket:itemAdded - New item added to basket
 * - basket:itemUpdated - Existing item quantity/notes changed
 * - basket:itemRemoved - Item removed from basket
 * - basket:update - Full basket refresh needed
 * - basket:reset - Basket cleared (e.g., after order confirmation)
 * - basket:orderConfirmed - Orders were sent to suppliers
 *
 * Usage:
 * const { isConnected, subscribe, unsubscribe } = useBasketSocket({
 *   venueId,
 *   onBasketUpdate: () => dispatch(fetchOrders(venueId)),
 *   onOrderConfirmed: (data) => handleOrderConfirmed(data),
 * })
 */

import { useEffect, useCallback, useRef, useState } from 'react'
import { io } from 'socket.io-client'
import config from 'src/config'

// Basket event types - keep in sync with backend
const BASKET_EVENTS = {
  ITEM_ADDED: 'basket:itemAdded',
  ITEM_UPDATED: 'basket:itemUpdated',
  ITEM_REMOVED: 'basket:itemRemoved',
  BASKET_RESET: 'basket:reset',
  BASKET_UPDATE: 'basket:update',
  ORDER_CONFIRMED: 'basket:orderConfirmed',
}

// Get the WebSocket URL from config
const getSocketUrl = () => {
  const url = config.apiGateway.EXPRESS_URL
  return url ? url.replace('/api/v1/', '') : null
}

/**
 * Custom hook for real-time basket synchronization
 */
const useBasketSocket = ({
  venueId,
  userId = null,
  onBasketUpdate = () => {},
  onItemAdded = () => {},
  onItemUpdated = () => {},
  onItemRemoved = () => {},
  onBasketReset = () => {},
  onOrderConfirmed = () => {},
  enabled = true,
}) => {
  const socketRef = useRef(null)
  const [isConnected, setIsConnected] = useState(false)
  const [lastEvent, setLastEvent] = useState(null)
  const reconnectAttemptsRef = useRef(0)
  const maxReconnectAttempts = 3

  // Store callbacks in refs to prevent reconnection loops
  const callbacksRef = useRef({
    onBasketUpdate,
    onItemAdded,
    onItemUpdated,
    onItemRemoved,
    onBasketReset,
    onOrderConfirmed,
  })

  // Update refs when callbacks change (without triggering reconnection)
  useEffect(() => {
    callbacksRef.current = {
      onBasketUpdate,
      onItemAdded,
      onItemUpdated,
      onItemRemoved,
      onBasketReset,
      onOrderConfirmed,
    }
  }, [
    onBasketUpdate,
    onItemAdded,
    onItemUpdated,
    onItemRemoved,
    onBasketReset,
    onOrderConfirmed,
  ])

  // Initialize socket connection
  const initializeSocket = useCallback(() => {
    if (!enabled || !venueId) {
      return null
    }

    const socketUrl = getSocketUrl()
    if (!socketUrl) {
      console.warn('WebSocket URL not configured')
      return null
    }

    console.log('[BasketSocket] Initializing socket connection to:', socketUrl)

    try {
      const socket = io(socketUrl, {
        query: { userId, venueId },
        transports: ['websocket'],
        timeout: 10000,
        reconnection: false, // Disable auto-reconnection to prevent loops
      })

      return socket
    } catch (error) {
      console.warn(
        '[BasketSocket] Failed to initialize WebSocket:',
        error.message,
      )
      return null
    }
  }, [enabled, venueId, userId])

  // Setup event handlers
  const setupEventHandlers = useCallback(
    socket => {
      if (!socket) return

      socket.on('connect', () => {
        console.log('[BasketSocket] Connected')
        setIsConnected(true)
        reconnectAttemptsRef.current = 0

        // Subscribe to venue basket updates
        socket.emit('basket:subscribe', { venueId })
      })

      socket.on('disconnect', reason => {
        console.log('[BasketSocket] Disconnected:', reason)
        setIsConnected(false)
      })

      socket.on('connect_error', error => {
        console.warn('[BasketSocket] Connection error:', error.message)
        setIsConnected(false)
        reconnectAttemptsRef.current++

        if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          console.warn(
            '[BasketSocket] Max reconnection attempts reached, giving up',
          )
        }
      })

      // Basket event handlers - use refs to prevent reconnection loops
      socket.on(BASKET_EVENTS.ITEM_ADDED, data => {
        console.log('[BasketSocket] Item added:', data)
        setLastEvent({ type: 'itemAdded', data, timestamp: Date.now() })
        callbacksRef.current.onItemAdded(data)
        callbacksRef.current.onBasketUpdate(data) // Trigger refetch
      })

      socket.on(BASKET_EVENTS.ITEM_UPDATED, data => {
        console.log('[BasketSocket] Item updated:', data)
        setLastEvent({ type: 'itemUpdated', data, timestamp: Date.now() })
        callbacksRef.current.onItemUpdated(data)
        callbacksRef.current.onBasketUpdate(data) // Trigger refetch
      })

      socket.on(BASKET_EVENTS.ITEM_REMOVED, data => {
        console.log('[BasketSocket] Item removed:', data)
        setLastEvent({ type: 'itemRemoved', data, timestamp: Date.now() })
        callbacksRef.current.onItemRemoved(data)
        callbacksRef.current.onBasketUpdate(data) // Trigger refetch
      })

      socket.on(BASKET_EVENTS.BASKET_UPDATE, data => {
        console.log('[BasketSocket] Basket update:', data)
        setLastEvent({ type: 'update', data, timestamp: Date.now() })
        callbacksRef.current.onBasketUpdate(data)
      })

      socket.on(BASKET_EVENTS.BASKET_RESET, data => {
        console.log('[BasketSocket] Basket reset:', data)
        setLastEvent({ type: 'reset', data, timestamp: Date.now() })
        callbacksRef.current.onBasketReset(data)
        callbacksRef.current.onBasketUpdate(data) // Trigger refetch to clear basket
      })

      socket.on(BASKET_EVENTS.ORDER_CONFIRMED, data => {
        console.log('[BasketSocket] Order confirmed:', data)
        setLastEvent({ type: 'orderConfirmed', data, timestamp: Date.now() })
        callbacksRef.current.onOrderConfirmed(data)
        callbacksRef.current.onBasketUpdate(data) // Trigger refetch to update basket
      })
    },
    [venueId],
  )

  // Connect and setup on mount / when venueId changes
  useEffect(() => {
    if (!enabled || !venueId) {
      return
    }

    const socket = initializeSocket()
    if (socket) {
      socketRef.current = socket
      setupEventHandlers(socket)
    }

    // Cleanup on unmount or venueId change
    return () => {
      if (socketRef.current) {
        // Unsubscribe from venue basket
        socketRef.current.emit('basket:unsubscribe', { venueId })
        socketRef.current.disconnect()
        socketRef.current = null
      }
      setIsConnected(false)
    }
    // Only reconnect when venueId or enabled changes, not when callbacks change
    // Dependencies intentionally limited to prevent reconnection loops
  }, [enabled, venueId])

  // Manual subscribe function
  const subscribe = useCallback(newVenueId => {
    if (socketRef.current && newVenueId) {
      socketRef.current.emit('basket:subscribe', { venueId: newVenueId })
    }
  }, [])

  // Manual unsubscribe function
  const unsubscribe = useCallback(oldVenueId => {
    if (socketRef.current && oldVenueId) {
      socketRef.current.emit('basket:unsubscribe', { venueId: oldVenueId })
    }
  }, [])

  // Force reconnect
  const reconnect = useCallback(() => {
    console.log('[BasketSocket] Manual reconnect triggered')
    if (socketRef.current) {
      if (socketRef.current.connected) {
        socketRef.current.disconnect()
      }
      socketRef.current.connect()
    }
  }, [])

  // Emit basket events (call these after successful mutations)
  const emitItemAdded = useCallback(
    order => {
      if (socketRef.current && venueId) {
        socketRef.current.emit(BASKET_EVENTS.ITEM_ADDED, {
          venueId,
          order,
          timestamp: new Date().toISOString(),
        })
        console.log('[BasketSocket] Emitted itemAdded event')
      }
    },
    [venueId],
  )

  const emitItemUpdated = useCallback(
    order => {
      if (socketRef.current && venueId) {
        socketRef.current.emit(BASKET_EVENTS.ITEM_UPDATED, {
          venueId,
          order,
          timestamp: new Date().toISOString(),
        })
        console.log('[BasketSocket] Emitted itemUpdated event')
      }
    },
    [venueId],
  )

  const emitItemRemoved = useCallback(
    orderId => {
      if (socketRef.current && venueId) {
        socketRef.current.emit(BASKET_EVENTS.ITEM_REMOVED, {
          venueId,
          orderId,
          timestamp: new Date().toISOString(),
        })
        console.log('[BasketSocket] Emitted itemRemoved event')
      }
    },
    [venueId],
  )

  const emitBasketUpdate = useCallback(
    (reason = 'update') => {
      if (socketRef.current && venueId) {
        socketRef.current.emit(BASKET_EVENTS.BASKET_UPDATE, {
          venueId,
          reason,
          timestamp: new Date().toISOString(),
        })
        console.log('[BasketSocket] Emitted basketUpdate event')
      }
    },
    [venueId],
  )

  const emitOrderConfirmed = useCallback(
    confirmedSuppliers => {
      if (socketRef.current && venueId) {
        socketRef.current.emit(BASKET_EVENTS.ORDER_CONFIRMED, {
          venueId,
          confirmedSuppliers,
          timestamp: new Date().toISOString(),
        })
        console.log('[BasketSocket] Emitted orderConfirmed event')
      }
    },
    [venueId],
  )

  return {
    isConnected,
    lastEvent,
    subscribe,
    unsubscribe,
    reconnect,
    socket: socketRef.current,
    // Emit functions - call these after successful mutations
    emitItemAdded,
    emitItemUpdated,
    emitItemRemoved,
    emitBasketUpdate,
    emitOrderConfirmed,
  }
}

export { BASKET_EVENTS }
export default useBasketSocket
